import React, { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImage } from "../lib/api";

/** Horizontal drag-scroll carousel of design shots (phone/desktop mockups). */
const DesignShots = ({ shots = [] }) => {
  const scrollerRef = useRef(null);
  const [progress, setProgress] = useState(0);

  if (!shots || shots.length === 0) return null;

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const total = el.scrollWidth - el.clientWidth;
    setProgress(total > 0 ? (el.scrollLeft / total) * 100 : 0);
  };

  const scrollBy = (dx) => {
    scrollerRef.current?.scrollBy({ left: dx, behavior: "smooth" });
  };

  return (
    <section id="shots" className="relative py-16 md:py-24 bg-[color:var(--bg)]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center mb-8 md:mb-10">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--muted)]">
            Showcase
          </div>
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold leading-[1.05]">
            Latest design <em className="italic text-[color:var(--violet)]">shots</em>.
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-[color:var(--ink-soft)]">
            Little visual experiments &mdash; mockups, illustrations and product screens I&apos;ve
            been playing with.
          </p>
        </div>

        <div className="relative">
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => scrollBy(-320)}
            className="hidden md:grid place-items-center absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-[color:var(--card)] border border-[color:var(--card-border)] shadow-md hover:bg-[color:var(--violet)] hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => scrollBy(320)}
            className="hidden md:grid place-items-center absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-[color:var(--card)] border border-[color:var(--card-border)] shadow-md hover:bg-[color:var(--violet)] hover:text-white transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div
            ref={scrollerRef}
            onScroll={onScroll}
            data-testid="design-shots-scroller"
            className="flex gap-4 md:gap-5 overflow-x-auto no-scrollbar pb-4 snap-x snap-mandatory"
          >
            {shots.map((s, i) => (
              <div
                key={i}
                className="snap-start shrink-0 w-[75vw] sm:w-[46vw] md:w-[300px] lg:w-[320px] rounded-[22px] overflow-hidden border border-[color:var(--card-border)] bg-[color:var(--card)] mk-card"
              >
                {s.image && (
                  <img
                    src={resolveImage(s.image)}
                    alt={s.title || `Design shot ${i + 1}`}
                    className="w-full aspect-[3/4] object-cover"
                  />
                )}
                {(s.title || s.caption) && (
                  <div className="px-4 py-3">
                    {s.title && (
                      <div className="font-display italic font-semibold text-base truncate">
                        {s.title}
                      </div>
                    )}
                    {s.caption && (
                      <div className="text-xs text-[color:var(--muted)] mt-0.5 line-clamp-2">
                        {s.caption}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mk-scroll-progress mt-4 max-w-md mx-auto">
            <span style={{ width: `${Math.max(8, progress)}%` }} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default DesignShots;
